/**
 * Multilingual NLU (English/Hindi/Hinglish)
 * Output: { intent, language, slots:{ layer, size, placement, rotation, repeat } }
 */

export function parseText(raw) {
  const language = detectLanguage(raw);
  const text0 = toLatinDigits(String(raw || "").trim());
  const text = " " + normalizeSeparators(text0.toLowerCase()) + " ";

  // intent: keywords OR (size + coords) heuristics
  const isRect = /(rectangle|rect|आयत|chauk|chauka|square|ban(a|ao|ade|ado)|bana\s*do|bana\s*de|draw|create|kar\s*do|tayyar\s*kar)/.test(text);
  const isDelete = /(delete|remove|erase|mit(a|ao)|hata(o|do)|ud[aā] do)/.test(text);

  const hasSize = hasSizeHint(text);
  const hasCoordPair = hasCoordHint(text);
  let intent = "unknown";
  if (isRect || (hasSize && hasCoordPair)) intent = "draw_rectangle";
  if (isDelete) intent = "delete_rectangle";


  const layer = resolveLayer(text);
  const size = parseSize(text);
  const placement = parsePlacement(text);
  const deletion = parseDeletionFilters(text); // {selected, ids[], region:{p1,p2}, size:{w,h,units}}
  const rotation = parseRotation(text);
  const repeat = parseRepeat(text);

  return { intent, language, slots: { layer, size, placement, rotation, repeat, deletion } };
}

/* ───────── utils ───────── */

export function detectLanguage(s) {
  if (!s) return "hinglish";
  const hasHindi = /[\u0900-\u097F]/.test(s);
  const hasLatin = /[A-Za-z]/.test(s);
  if (hasHindi && hasLatin) return "hinglish";
  if (hasHindi) return "hi";
  if (hasLatin) return "en";
  return "hinglish";
}

export function toLatinDigits(s) {
  const map = {"०":"0","१":"1","२":"2","३":"3","४":"4","५":"5","६":"6","७":"7","८":"8","९":"9","٠":"0","١":"1","٢":"2","٣":"3","٤":"4","٥":"5","٦":"6","٧":"7","٨":"8","٩":"9"};
  return s.replace(/[\u0966-\u096F\u0660-\u0669]/g, (c) => map[c] ?? c).replace(/[，]/g, ",");
}

function normalizeSeparators(s) {
  // compress multiple spaces, normalize “by/x/×”, tolerate “ka/का/size”
  return s
    .replace(/[*]/g, "x")
    .replace(/[×]/g, "x")
    .replace(/\s+ka\b/g, " ")
    .replace(/\bka\s+/g, " ")
    .replace(/\bsize\s+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ───────── layers ───────── */

const LAYER_SYNONYMS = {
  m1: ["m1","metal1","metal-1","m-1"], m2: ["m2","metal2","metal-2","m-2"], m3: ["m3","metal3","metal-3","m-3"],
  v1: ["v1","via1","via-1"], v2: ["v2","via2","via-2"],
  nw1:["nw","nw1","nwell","n-well","n well"], pw1:["pw","pw1","pwell","p-well","p well"],
  poly:["poly","polysilicon","gate"], diff:["diff","active"], cont:["cont","contact"]
};
function resolveLayer(text) {
  const m = text.match(/\b(\d+)\s*[:/]\s*(\d+)\b/);
  if (m) return `${m[1]}:${m[2]}`;
  for (const [canon, variants] of Object.entries(LAYER_SYNONYMS)) {
    const re = new RegExp(`\\b(${variants.map(esc).join("|")})\\b`, "i");
    if (re.test(text)) return canon;
  }
  return null;
}
function esc(s){return s.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");}

/* ───────── numbers/units ───────── */

const UNIT_RE = "(nm|um|µm|mm)";
function normUnits(u){ if(!u) return null; const x=u.toLowerCase(); return x==="µm"?"um":x; }

/* ───────── size parsing ───────── */

function hasSizeHint(text){
  return /\b-?\d+(?:\.\d+)?\s*(?:x|by)\s*-?\d+(?:\.\d+)?\b/.test(text)
      || /\b(width|height|चौड़ाई|ऊँचाई|lambai|chaudai)\b/.test(text)
      || /\bsquare\s+-?\d+(?:\.\d+)?\b/.test(text);
}

function parseSize(text) {
  // square N um / “square 2”
  let m = text.match(/\b(square|sakua?r(e)?)\s+(-?\d+(?:\.\d+)?)\s*(nm|um|µm|mm)?\b/i);
  if (m) return { square: parseFloat(m[3]), units: normUnits(m[4]) };

  // WxH: handles “2um x 1um”, “2 x 1 um”, “2 by 1”, “2x1”
  m = text.match(new RegExp(`\\b(-?\\d+(?:\\.\\d+)?)\\s*(?:${UNIT_RE})?\\s*(?:x|by)\\s*(-?\\d+(?:\\.\\d+)?)\\s*(?:${UNIT_RE})?\\b`, "i"));
  if (m) {
    const w = parseFloat(m[1]);
    const u1 = normUnits(m[2]);
    const h = parseFloat(m[3]);
    const u2 = normUnits(m[4]);
    return { w, h, units: u2 || u1 || null };
  }

  // named width/height (any order)
  const w = matchNumberAfter(text, /\b(width|चौड़ाई|chaudai|chowdai)\b/i);
  const h = matchNumberAfter(text, /\b(height|ऊँचाई|uchai|lambai|lambei)\b/i);
  if (w || h) return { w: w?.value ?? null, h: h?.value ?? null, units: w?.units || h?.units || null };

  return null;
}
function parseDeletionFilters(text){
  const out = {};
  // selected
  if (/\b(selected|current selection)\b/.test(text)) out.selected = true;

  // ids: "id 123", "ids 1,2,3"
  const mid = text.match(/\bids?\b\s*([0-9,\s-]+)/i);
  if (mid) {
    out.ids = mid[1].split(/[, ]+/).filter(Boolean).map(n => Number(n));
  }

  // region: "from x1,y1 to x2,y2"
  const mr = text.match(/\bfrom\s+(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s+to\s+(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\b/i);
  if (mr) out.region = { p1:[+mr[1],+mr[2]], p2:[+mr[3],+mr[4]] };

  // coordinates + size also acceptable as a “box”
  // e.g., "box 0,0 50,50"
  const mb = text.match(/\bbox\b.*?(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?).*?(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/i);
  if (mb) out.region = { p1:[+mb[1],+mb[2]], p2:[+mb[3],+mb[4]] };

  // by size: "5 by 5", "width 5 height 5"
  const s = parseSize(text);
  if (s?.w && s?.h) out.size = { w:s.w, h:s.h, units:s.units||null };

  // cell name: "cell azeem"
  const mc = text.match(/\bcell\s+([A-Za-z0-9_:-]+)\b/i);
  if (mc) out.cell = mc[1];

  return Object.keys(out).length ? out : null;
}

function matchNumberAfter(text, headRe) {
  const idx = text.search(headRe);
  if (idx === -1) return null;
  const tail = text.slice(idx);
  const r = new RegExp(`(-?\\d+(?:\\.\\d+)?)(?:\\s*${UNIT_RE})?`, "i");
  const m = tail.match(r);
  if (!m) return null;
  return { value: parseFloat(m[1]), units: normUnits(m[2]) };
}

/* ───────── placement parsing ───────── */

function hasCoordHint(text){
  return /from\s+-?\d+(?:\.\d+)?\s*[, ]\s*-?\d+(?:\.\d+)?\s+to\s+-?\d+(?:\.\d+)?\s*[, ]\s*-?\d+(?:\.\d+)?/i.test(text)
      || /\bcoord(?:inate|inates|s)?\b.*?-?\d+(?:\.\d+)?\s*[, ]\s*-?\d+(?:\.\d+)?/.test(text)
      || /-?\d+(?:\.\d+)?\s*[, ]\s*-?\d+(?:\.\d+)?/.test(text)
      || /\bx\s*=\s*-?\d+(?:\.\d+)?\b.*\by\s*=\s*-?\d+(?:\.\d+)?\b/.test(text);
}

function parsePlacement(text) {
  // two points
  let m = text.match(/\bfrom\s+(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s+to\s+(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\b/i);
  if (m) return { mode: "two_points", p1: [num(m[1]), num(m[2])], p2: [num(m[3]), num(m[4])] };

  // center
  m = text.match(/\b(center|beech|के\s*बीच)\s*(?:at)?\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\b/i);
  if (m) return { mode: "center", center: [num(m[2]), num(m[3])] };

  // keyword -> coords
  m = text.match(/\b(at|pe|par|पर|पे)\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\b/i);
  if (m) return { mode: "origin", point: [num(m[2]), num(m[3])] };

  // coords -> keyword (“0,0 pe” / “0 0 at”)
  m = text.match(/\b(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)[\s,]*(?:at|pe|par|पर|पे|coordinates?)\b/i);
  if (m) return { mode: "origin", point: [num(m[1]), num(m[2])] };

  // “coordinates 2,6” / “coords 2 6”
  m = text.match(/\bcoord(?:inate|inates|s)?\b.*?(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/i);
  if (m) return { mode: "origin", point: [num(m[1]), num(m[2])] };

  // x=.. y=..
  const mx = text.match(/\bx\s*=\s*(-?\d+(?:\.\d+)?)/i);
  const my = text.match(/\by\s*=\s*(-?\d+(?:\.\d+)?)/i);
  if (mx && my) return { mode: "origin", point: [num(mx[1]), num(my[1])] };

  // single pair anywhere (comma OR space), but not ranges
  const pairs = [...text.matchAll(/(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/g)];
  if (pairs.length === 1) {
    const p = pairs[0]; return { mode: "origin", point: [num(p[1]), num(p[2])] };
  }
  return null;
}

function num(s){ return parseFloat(s); }

/* ───────── rotation/repeat ───────── */

function parseRotation(text){
  const m = text.match(/\b(rot(ation)?|rotate)\s*(-?\d+(?:\.\d+)?)\s*(deg|degree|°)?\b/i);
  return m ? parseFloat(m[3]) : null;
}

function parseRepeat(text){
  let m = text.match(/\b(repeat|array)\s+(\d+)\s*(?:x|by)\s*(\d+)\b/i);
  const base = m ? { nx: parseInt(m[2],10), ny: parseInt(m[3],10) } : null;

  const mdx = text.match(new RegExp(`\\bdx\\s*=\\s*(-?\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?`, "i"));
  const mdy = text.match(new RegExp(`\\bdy\\s*=\\s*(-?\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?`, "i"));
  const msp = text.match(new RegExp(`\\bspacing\\s+(-?\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?\\s+(-?\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?`, "i"));

  if (!base && !mdx && !mdy && !msp) return null;

  const dx = mdx ? parseFloat(mdx[1]) : msp ? parseFloat(msp[1]) : 0;
  const dy = mdy ? parseFloat(mdy[1]) : msp ? parseFloat(msp[3]) : 0;
  const units = normUnits(mdx?.[2] || mdy?.[2] || msp?.[2] || msp?.[4] || null);
  return { nx: base?.nx ?? 1, ny: base?.ny ?? 1, dx, dy, units };
}

export const __testonly = { resolveLayer, parseSize, parsePlacement, parseRotation, parseRepeat };
